/*
 * File: Footer.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 2, 2025 at 12:40 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { renderPrimaryResource } from '../../utilities/resources';
import { isValidNode, parseMarkdownLinks } from '../../utilities/parser';

// Constants
import { AVAILABLE_I18N_NAMESPACES, DEFAULT_COUNTRY } from '../../../constants/base.constants';

// Components
import { Padding } from '../Padding';
import { Typography } from '../Typography';
import { LocaleLink } from '../LocaleLink';

// Styles
import * as S from './Footer.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const Footer = ({
	className
}) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get resources manifest from hook
	const resourceManifest = useSelector((state) => state.resourceManifest.resources);

	// Get primary resource from manifest
	const primaryResource = useMemo(() => {
		if (resourceManifest && resourceManifest.length > 0) {
			const countryResource = resourceManifest.find((resource) => resource.reference_countries.includes(locale.countryId));
			if (countryResource) {
				return countryResource;
			}
			return resourceManifest.find((resource) => resource.reference_countries.includes(DEFAULT_COUNTRY)) || resourceManifest[0];
		}
		return null;
	}, [locale, resourceManifest]);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get legal urls
	const termsUrl = useMemo(() => (partner?.legal?.links?.terms ? partner?.legal?.links?.terms : `https://www.ourwave.org${locale.localePath}/terms`), [partner, locale]);
	const privacyUrl = useMemo(() => (partner?.legal?.links?.privacy ? partner?.legal?.links?.privacy : `https://www.ourwave.org${locale.localePath}/privacy`), [partner, locale]);
	const communityGuidelinesUrl = useMemo(() => (partner?.legal?.links?.community_guidelines ? partner?.legal?.links?.community_guidelines : `https://www.ourwave.org${locale.localePath}/community-guidelines`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Get partner content
	const attributionContent = useMemo(() => partner?.content?.attribution, [partner]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render primary resource
	const renderedPrimaryResource = useMemo(() => renderPrimaryResource(primaryResource, locale, t), [primaryResource, locale, t]);

	// Render component
	return (
		<S.Wrapper className={className}>
			<Padding left right>

				{/* Primary Resource */}
				{renderedPrimaryResource}

				<Typography tag="p" variation="3">
					{!attributionContent ? (
						<Trans t={t} i18nKey="attribution">
							Made with
							<FontAwesomeIcon icon={['fas', 'heart']} className="heart" style={{ margin: '0px 4px' }} />
							in Raleigh, NC
						</Trans>
					) : ReactParser.parseWithInstructions(parseMarkdownLinks(attributionContent[locale.localeBaseId]), isValidNode, ParsingInstructions)}
				</Typography>

				{/* Divider */}
				<Typography tag="p" variation="3">|</Typography>

				{/* Legal */}
				<Typography tag="p" variation="3">
					<Trans t={t} i18nKey="legalFooter">
						Read our
						{' '}
						<LocaleLink to={communityGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Community Guidelines</LocaleLink>
						,
						{' '}
						<LocaleLink to={privacyUrl} target={!hasInternalLegalPages ? '_blank' : null}>Privacy Policy</LocaleLink>
						, and
						{' '}
						<LocaleLink to={termsUrl} target={!hasInternalLegalPages ? '_blank' : null}>Terms</LocaleLink>
					</Trans>
				</Typography>
			</Padding>
		</S.Wrapper>
	);
};


/**
 * Configuration
 */

Footer.displayName = 'Footer';
Footer.propTypes = {
	className: PropTypes.string
};
Footer.defaultProps = {
	className: null,
};
